import React, { useContext } from "react";
import moment from "moment";
import { IntlContext } from "context";
import { AdInterface } from "types/ad";
import { Calendar, Clock, MapPin, RefreshCw } from "lucide-react";
import Image from "components/File/Image";
import ShareAdButton from "./ShareAdButton";
import ReportAdButton from "./ReportAdButton";
import FavoriteAdButton from "components/Favorite/FavoriteAdButton";
import { AdCategoryBadge } from "../AdCategoryBadge";
import { renderListingCategoryData } from "components/ListingDetail/renderListingCategoryData";

type Props = {
    ad: AdInterface;
    isRecurring: boolean;
    cityLabel: string;
};

const AdMainCard: React.FC<Props> = ({ ad, isRecurring, cityLabel }) => {
    const intl = useContext(IntlContext);

    return (
        <div className="bg-white rounded-[32px] border border-[#f1f5f9] drop-shadow-[0px_10px_20px_rgba(0,0,0,0.04)] p-6 sm:p-8">
            <div className="flex justify-between items-start mb-4">
                <span
                    className={`inline-flex items-center gap-2 text-[10px] leading-4 font-black uppercase tracking-[1px] px-3 py-1.5 rounded-full ${
                        isRecurring ? "bg-indigo-50 text-indigo-600" : "bg-amber-50 text-amber-600"
                    }`}
                >
                    {isRecurring ? <RefreshCw className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                    <span>
                        {isRecurring
                            ? intl.formatMessage("ad.card.frequency_recurring")
                            : intl.formatMessage("ad.card.frequency_one_time")}
                    </span>
                </span>
                <div className="flex gap-2">
                    <FavoriteAdButton ad={ad} />
                    <ReportAdButton adId={ad.id} />
                    <ShareAdButton ad={ad} />
                </div>
            </div>

            <h1 className="text-[34px] font-black text-[#1a1c2d] tracking-[-1.1px] leading-[1.4] mb-4">{ad.title}</h1>

            <div className="flex flex-wrap items-center gap-2 mb-6">
                <AdCategoryBadge category={String(ad.category)} label={String(ad.category)} />
                {ad.subcategory && (
                    <AdCategoryBadge category={String(ad.category)} label={String(ad.subcategory)} isSubcategory />
                )}
                {cityLabel && (
                    <span className="inline-flex items-center gap-2 text-[#5a607c] text-[13px] font-bold">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-[#ecfdf5] rounded-[10px] shrink-0">
                            <MapPin className="w-4 h-4 text-[#10b981]" />
                        </span>
                        {cityLabel}
                    </span>
                )}
                <span className="inline-flex items-center gap-2 text-[#5a607c] text-[13px] font-bold">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-[#f8fafc] border border-[#f1f5f9] rounded-[10px] shrink-0">
                        <Calendar className="w-4 h-4 text-[#5a607c]" />
                    </span>
                    {moment(ad.created_at).fromNow()}
                </span>
            </div>

            <div className="flex items-center gap-3 mb-3">
                <div className="w-1 h-4 bg-primary rounded-full shrink-0" />
                <h2 className="text-[14px] font-black tracking-[2.1px] uppercase text-foreground">
                    {intl.formatMessage("ad.view.description")}
                </h2>
            </div>
            <p className="text-[#5a607c] text-[15px] font-medium leading-[1.625] whitespace-pre-line mb-6">
                {ad.description}
            </p>

            {/* subcategory-specific data */}
            <div className="mb-2 italic text-muted-foreground flex flex-row gap-2">
                {renderListingCategoryData(
                    String(ad.category),
                    ad.subcategory != null ? String(ad.subcategory) : undefined,
                    { subcategory: ad.subcategory, data: ad.data },
                    "ad",
                )}
            </div>

            {/* duration / photo */}
            {ad.duration && (
                <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-gray-100 text-sm text-gray-600">
                    <div>
                        <span className="font-semibold text-foreground">
                            {intl.formatMessage("ad.view.duration_label")} :
                        </span>{" "}
                        {intl.formatMessage("ad.view.duration_hours", { duration: ad.duration })}
                    </div>
                </div>
            )}

            {ad.picture_id && (
                <div className="mt-4">
                    <Image className="rounded-xl max-h-72 object-cover" id={ad.picture_id} />
                </div>
            )}
        </div>
    );
};

export default AdMainCard;
